// src/AuthContext.js

import React, { createContext, useState, useContext, useEffect, useCallback } from 'react'; // Added useCallback
import axiosInstance, { setAuthToken, setupAxiosInterceptor } from './api/axios';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();
const GLOBAL_PROJECT_KEY = 'globalSelectedProjectName'; // Universal key

export const useAuth = () => {
    return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
    const [accessToken, setAccessToken] = useState(null);
    const [setupToken, setSetupToken] = useState(null);
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loginUsername, setLoginUsername] = useState(null);
    const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);
    // --- NEW STATE ---
    const [selectedProjectName, setSelectedProjectName] = useState('');
    // ---------------
    const navigate = useNavigate();

    // --- NEW HELPER --- Find initial project (localStorage > first project > empty)
    const determineInitialProject = useCallback((userProjects) => {
        if (!userProjects || userProjects.length === 0) {
            return '';
        }
        const savedProject = localStorage.getItem(GLOBAL_PROJECT_KEY);
        if (savedProject && userProjects.some(p => p.project_name === savedProject)) {
            return savedProject;
        }
        return userProjects[0].project_name; // Default to first
    }, []);
    // ------------------

    const logoutUser = async () => {
        try {
            await axiosInstance.post('/logout');
        } catch (error) {
            console.error("Logout failed:", error);
        } finally {
            setAccessToken(null);
            setUser(null);
            setAuthToken(null);
            setLoginUsername(null);
            setRequiresPasswordChange(false);
            // --- CLEAR PROJECT STATE ---
            setSelectedProjectName('');
            localStorage.removeItem(GLOBAL_PROJECT_KEY);
            // -------------------------
            navigate('/');
        }
    };

    const getNewAccessToken = async () => {
        // ... (keep existing code)
        const response = await axiosInstance.post('/token/refresh');
        const newAccessToken = response.data.access_token;
        setAccessToken(newAccessToken);
        setAuthToken(newAccessToken);
        return newAccessToken;
    };

    // --- NEW FUNCTION --- Update and persist selected project
    const updateSelectedProject = useCallback((projectName) => {
        setSelectedProjectName(projectName);
        if (projectName) {
            localStorage.setItem(GLOBAL_PROJECT_KEY, projectName);
        } else {
            localStorage.removeItem(GLOBAL_PROJECT_KEY);
        }
    }, []);
    // --------------------

    useEffect(() => {
        const handleLogout = () => {
            setAccessToken(null);
            setUser(null);
            setAuthToken(null);
            setLoginUsername(null);
            // --- CLEAR PROJECT STATE on interceptor logout ---
            setSelectedProjectName('');
            localStorage.removeItem(GLOBAL_PROJECT_KEY);
            // ---------------------------------------------
            if (window.location.pathname !== '/') {
                navigate('/');
            }
        };
        setupAxiosInterceptor(handleLogout, getNewAccessToken);

        const tryRefreshToken = async () => {
            try {
                await getNewAccessToken();
                const userResponse = await axiosInstance.get('/api/users/me');
                const userData = userResponse.data;
                setUser(userData);
                // --- SET INITIAL PROJECT ON REFRESH ---
                const initialProject = determineInitialProject(userData.projects);
                setSelectedProjectName(initialProject);
                // -------------------------------------
            } catch (error) {
                console.log("No active session or session expired.");
                // Ensure project state is cleared if refresh fails
                setSelectedProjectName('');
                localStorage.removeItem(GLOBAL_PROJECT_KEY);
            } finally {
                setIsLoading(false);
            }
        };

        tryRefreshToken();
        // Add determineInitialProject to dependencies
    }, [navigate, determineInitialProject]);

    const login = async (credentials) => {
        try {
            // ... (keep existing formDetails setup)
             const formDetails = new URLSearchParams();
             formDetails.append('username', credentials.username);
             formDetails.append('password', credentials.password);

            const response = await axiosInstance.post('/token', formDetails);

            if (response.data.message === "2FA setup required") {
                // ... (keep existing 2FA setup logic)
                 setLoginUsername(credentials.username);
                 setSetupToken(response.data.setup_token); // <-- SAVE THE TOKEN
                 return { success: false, requires2faSetup: true };
            }

            if (response.data.access_token) {
                const token = response.data.access_token;
                setAccessToken(token);
                setAuthToken(token);
                const userResponse = await axiosInstance.get('/api/users/me');
                const userData = userResponse.data;
                setUser(userData);
                // --- SET INITIAL PROJECT ON LOGIN ---
                const initialProject = determineInitialProject(userData.projects);
                setSelectedProjectName(initialProject);
                // ----------------------------------
                setLoginUsername(null);
                return { success: true, requires2fa: false };
            }
            else if (response.status === 200 && response.data.message === "2FA verification required") {
                 // The challenge is what proves the password step happened; the OTP
                 // step sends it instead of a username. sessionStorage rather than
                 // localStorage: it is short-lived and must not outlive the tab.
                 setLoginUsername(credentials.username);
                 sessionStorage.setItem('pending_2fa_challenge', response.data.challenge_token);
                 return { success: false, requires2fa: true }; // Return result
            }
        } catch (error) {
            // ... (keep existing error handling for password expiry etc.)
            if (error.response?.status === 403 && error.response?.data?.detail === "Password expired. Please change your password.") {
                setLoginUsername(credentials.username); // Store username for the change password page
                setRequiresPasswordChange(true); // Set our special flag
                return { success: false, requiresPasswordChange: true }; // Return a specific state
            }
            throw error;
        }
    };

    const verifyOtpAndLogin = async (otp) => {
         const challengeToken = sessionStorage.getItem('pending_2fa_challenge');
         if (!challengeToken) {
             // No challenge means the password step was never completed in this
             // tab, so there is nothing for the OTP to complete.
             throw new Error("Session error. Please try logging in again.");
         }

         const response = await axiosInstance.post('/token/2fa', {
             challenge_token: challengeToken,
             otp
         });


        if (response.data.access_token) {
            const token = response.data.access_token;
            setAccessToken(token);
            setAuthToken(token);
            const userResponse = await axiosInstance.get('/api/users/me');
            const userData = userResponse.data;
            setUser(userData);
            // --- SET INITIAL PROJECT ON OTP LOGIN ---
            const initialProject = determineInitialProject(userData.projects);
            setSelectedProjectName(initialProject);
            // -------------------------------------
            sessionStorage.removeItem('pending_2fa_challenge');
            localStorage.removeItem('login_username_for_otp');
            setLoginUsername(null);
            return { success: true };
        }
    };

    const value = {
        accessToken,
        setupToken,
        user,
        isLoading,
        loginUsername,
        requiresPasswordChange,
        // The 2FA setup page sets this when a first login still has to replace
        // its admin-issued password: the change-password screen then runs in its
        // no-session mode, for the username login() stored.
        setRequiresPasswordChange,
        // --- EXPOSE NEW STATE & FUNCTION ---
        selectedProjectName,
        updateSelectedProject,
        // ---------------------------------
        login,
        verifyOtpAndLogin,
        logoutUser,
    };

    return (
        <AuthContext.Provider value={value}>
            {!isLoading && children} {/* Render children only when not loading initial auth state */}
        </AuthContext.Provider>
    );
};